/**
 * Localization keys for the durable-turn failures whose message is written
 * somewhere that has no locale.
 *
 * A hosted agent's engine settles a failed run with a stable machine `code`
 * and a human message it composes itself, in the container — English, always,
 * because the container knows nothing about the reader. Rendering that message
 * verbatim put "The agent's model budget is exhausted" in a Swedish thread.
 *
 * The answer already carries a localization channel for exactly this case:
 * `translation_key` / `translation_params`, which `system-message.tsx` prefers
 * over the raw message whenever a translation exists (the file-upload errors
 * use it). So this maps the codes worth translating onto locale keys and lets
 * everything else keep the engine's own words — an unmapped code still shows
 * its message rather than a blank card, and `t()` falls back to the message
 * when a key is missing from a locale.
 */

import i18n from "i18next"

import { ApiError } from "@/api/client/_shared"

/** Codes a run can settle with, mapped to their locale key. */
export const DURABLE_TURN_ERROR_TRANSLATION_KEYS: Readonly<Record<string, string>> = {
  // services/hosted_agents/quota.py -> 429 agent_budget_exceeded, classified by
  // the engine (services/hermes-nous/hermes_nous/driver.py::classify).
  agent_budget_exceeded: "agents.errors.budget_exceeded",
  // services/hosted_agents/runs.py, ERROR_* constants.
  hosted_agent_unknown: "agents.errors.agent_unknown",
  hosted_agent_disabled: "agents.errors.agent_disabled",
  hosted_agent_paused: "agents.errors.agent_paused",
  // Losing access to an agent is ONE situation for the reader, whether accept
  // refuses the turn (routes/chat_turns.py) or enqueue re-checks it
  // (runs.py ERROR_AGENT_ACCESS_REVOKED): both answer this one code (#3333).
  hosted_agent_access_revoked: "agents.errors.access_revoked",
  // routes/chat_turns.py refuses accept with 503 when an enabled agent's
  // container has never contacted the gateway, so no run would ever start.
  hosted_agent_not_running: "agents.errors.not_running",
  hosted_agent_dispatch_lost: "agents.errors.dispatch_lost",
  agent_delegated_identity_unavailable: "agents.errors.delegated_identity_unavailable",
}

/** The locale key for a settled turn's error code, or undefined to keep its message. */
export function durableTurnErrorTranslationKey(code: string | undefined): string | undefined {
  return code ? DURABLE_TURN_ERROR_TRANSLATION_KEYS[code] : undefined
}

/**
 * The text to SHOW for a settled turn's error.
 *
 * `system-message.tsx` resolves `translation_key` itself, but the agent
 * workspace renders the session's plain `error` string
 * (`agent-chat-view.tsx` → `Chat.Error`), which never passes through a
 * component that could translate it. Both paths must say the same thing, so
 * the string form is resolved here against the i18n instance directly — the
 * same non-hook access `citation-preview-sidebar.tsx` and
 * `local-file-preview-sidebar.tsx` already use.
 *
 * The engine's own message is the fallback in every failure mode: an unmapped
 * code, a key missing from this locale, or an i18n instance that is not ready.
 */
export function durableTurnErrorText(
  error: { message?: string; code?: string } | undefined
): string {
  const message = error?.message ?? ""
  const key = durableTurnErrorTranslationKey(error?.code)
  if (!key) return message
  const translated = i18n.t(key, { defaultValue: message })
  return typeof translated === "string" && translated ? translated : message
}

/**
 * The text to SHOW for a turn the backend refused at ACCEPT, or undefined when
 * the refusal carries no code worth translating.
 *
 * Accept refusals never become a settled turn: `use-chat.ts` writes the raw
 * `ApiError` into the request state, whose `toString()` is the transport's
 * "HTTP error! status: 403…" text. The backend's `{code}` is the same
 * vocabulary a settled run uses (an agent that was deleted, switched off, or
 * that the reader lost access to), so it resolves through the same table.
 */
export function durableTurnAcceptErrorText(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined
  const key = durableTurnErrorTranslationKey(error.code)
  if (!key) return undefined
  const fallback = error.bodyError || error.message
  const translated = i18n.t(key, { defaultValue: fallback })
  return typeof translated === "string" && translated ? translated : fallback
}
